import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Briefcase, FileText, MapPin, IndianRupee,
  Users, Calendar, ExternalLink, Mail, Phone, ChevronRight, Ban,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "", title: "", description: "", location: "",
  employment_type: "Full-time", qualification: "", salary_range: "",
  openings: 1, deadline: "", status: "Open",
};

function fmt(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
}
function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

export default function InstituteJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [appsFor, setAppsFor] = useState(null); // currently viewed job (or {id:'__general__', title:'General Applications'})
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [confirmDeleteApp, setConfirmDeleteApp] = useState(null);
  const [slug, setSlug] = useState("");
  const [generalCount, setGeneralCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [j, g] = await Promise.all([
        api.get("/institute/jobs"),
        api.get("/institute/job-applications/general").catch(() => ({ data: [] })),
      ]);
      setJobs(j.data);
      setGeneralCount((g.data || []).length);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load jobs");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/institute/info").then((r) => setSlug(r.data?.institute?.website_slug || "")).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm(emptyForm); setOpen(true); };
  const openEdit = (j) => {
    setForm({
      id: j.id, title: j.title || "", description: j.description || "",
      location: j.location || "", employment_type: j.employment_type || "Full-time",
      qualification: j.qualification || "", salary_range: j.salary_range || "",
      openings: j.openings || 1, deadline: (j.deadline || "").slice(0, 10),
      status: j.status || "Open",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const payload = {
      title: form.title.trim(),
      description: (form.description || "").trim(),
      location: (form.location || "").trim(),
      employment_type: EMPLOYMENT_TYPES.includes(form.employment_type) ? form.employment_type : "Full-time",
      qualification: (form.qualification || "").trim(),
      salary_range: (form.salary_range || "").trim(),
      openings: Math.max(1, Number(form.openings) || 1),
      deadline: (form.deadline || "").slice(0, 10),
      status: form.status === "Closed" ? "Closed" : "Open",
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/institute/jobs/${form.id}`, payload);
        toast.success("Job updated");
      } else {
        await api.post("/institute/jobs", payload);
        toast.success("Job posted");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/jobs/${confirmDelete.id}`);
      toast.success("Job removed (including applications)");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const viewApps = async (j) => {
    setAppsFor(j);
    setAppsLoading(true);
    try {
      const url = j.id === "__general__"
        ? `/institute/job-applications/general`
        : `/institute/jobs/${j.id}/applications`;
      const { data } = await api.get(url);
      setApps(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load applications");
    } finally { setAppsLoading(false); }
  };

  const removeApp = async () => {
    if (!confirmDeleteApp) return;
    try {
      await api.delete(`/institute/job-applications/${confirmDeleteApp.id}`);
      toast.success("Application removed");
      setConfirmDeleteApp(null);
      viewApps(appsFor);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const blocked = slug === "mitschool" || slug === "jirt";

  return (
    <div className="space-y-6" data-testid="institute-jobs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Institute CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Careers &amp; Job Board</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">
            Post job openings — candidates apply online with a resume (PDF, 500 KB max).
          </div>
        </div>
        {!blocked && (
          <button type="button" onClick={openAdd} data-testid="i-jobs-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Post New Job
          </button>
        )}
      </div>

      {blocked ? (
        <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-4 text-sm text-amber-900 flex items-start gap-3">
          <Ban size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Job postings are not enabled for this institute.</div>
            <div className="mt-1 text-xs">The Careers module is available to polytechnic and college institutes only.</div>
          </div>
        </div>
      ) : loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : (
        <>
          <div
            data-testid="i-general-apps-tile"
            className="bg-white border-l-4 border-[#d4a24c] border border-[#e5e0d8] rounded-sm p-4 md:p-5 flex flex-wrap items-center justify-between gap-3"
          >
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#d4a24c] font-bold">Talent Pool</div>
              <h3 className="font-serif text-lg text-[#1c1a17] mt-0.5">General Applications</h3>
              <div className="text-xs text-[#6b655b] mt-0.5">
                Candidates who sent their resume without applying to a specific job.
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#6b655b] font-semibold">Received</div>
                <div className="font-serif text-2xl text-[#234333] leading-none">{generalCount}</div>
              </div>
              <button
                type="button"
                onClick={() => viewApps({ id: "__general__", title: "General Applications" })}
                data-testid="i-general-apps-view"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#234333] bg-[#234333]/10 hover:bg-[#234333]/20 px-3 py-1.5 rounded-sm"
              >
                <FileText size={11} /> View
              </button>
            </div>
          </div>

          {jobs.length === 0 ? (
            <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
              <Briefcase size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
              <div className="mt-3 text-sm text-[#6b655b]">No jobs posted yet. Click <b>Post New Job</b> to add one.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((j) => (
            <div key={j.id} data-testid={`i-job-row-${j.id}`} className="bg-white border border-[#e5e0d8] rounded-sm p-4 md:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg text-[#1c1a17]">{j.title}</h3>
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold ${j.status === "Open" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{j.status}</span>
                    <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold bg-[#234333]/10 text-[#234333]">{j.employment_type}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#6b655b]">
                    {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{j.location}</span>}
                    {j.salary_range && <span className="inline-flex items-center gap-1"><IndianRupee size={11} />{j.salary_range}</span>}
                    <span className="inline-flex items-center gap-1"><Users size={11} />{j.openings} opening{j.openings > 1 ? "s" : ""}</span>
                    {j.deadline && <span className="inline-flex items-center gap-1"><Calendar size={11} />Apply by {fmtDate(j.deadline)}</span>}
                    <span className="inline-flex items-center gap-1 text-[#8b8578]">Posted {fmt(j.posted_at)}</span>
                  </div>
                  {j.qualification && (
                    <div className="mt-2 text-xs text-[#5b5650]">
                      <span className="font-semibold text-[#1c1a17]">Qualification:</span> {j.qualification}
                    </div>
                  )}
                  {j.description && <div className="mt-2 text-xs text-[#5b5650] line-clamp-2 whitespace-pre-line">{j.description}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => viewApps(j)} data-testid={`i-job-apps-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#234333] bg-[#234333]/10 hover:bg-[#234333]/20 px-3 py-1.5 rounded-sm">
                    <FileText size={11} /> Applications
                  </button>
                  <button type="button" onClick={() => openEdit(j)} data-testid={`i-job-edit-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#234333] hover:bg-[#234333]/10 px-2 py-1.5 rounded-sm">
                    <Pencil size={11} /> Edit
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(j)} data-testid={`i-job-delete-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 px-2 py-1.5 rounded-sm">
                    <Trash2 size={11} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
            </div>
          )}
        </>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Job" : "Post New Job"}</DialogTitle>
            <DialogDescription>Details shown on the public Careers page.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *" full>
              <input data-testid="i-job-input-title" type="text" maxLength={200} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field label="Employment Type">
              <select data-testid="i-job-input-type" className={inputCls} value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)}>
                {EMPLOYMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <input data-testid="i-job-input-location" type="text" maxLength={160} className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g., Aurangabad" />
            </Field>
            <Field label="Number of Openings">
              <input data-testid="i-job-input-openings" type="number" min={1} className={inputCls} value={form.openings} onChange={(e) => set("openings", e.target.value)} />
            </Field>
            <Field label="Application Deadline">
              <input data-testid="i-job-input-deadline" type="date" className={inputCls} value={form.deadline} onChange={(e) => set("deadline", e.target.value)} />
            </Field>
            <Field label="Salary Range">
              <input data-testid="i-job-input-salary" type="text" maxLength={160} className={inputCls} value={form.salary_range} onChange={(e) => set("salary_range", e.target.value)} placeholder="e.g., ₹40,000 - ₹60,000 per month" />
            </Field>
            <Field label="Qualification / Eligibility" full>
              <textarea data-testid="i-job-input-qual" rows={2} maxLength={1000} className={`${inputCls} resize-y`} value={form.qualification} onChange={(e) => set("qualification", e.target.value)} placeholder="e.g., M.Tech / Ph.D in relevant field" />
            </Field>
            <Field label="Description" full>
              <textarea data-testid="i-job-input-desc" rows={6} maxLength={6000} className={`${inputCls} resize-y`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Roles, responsibilities, expectations…" />
            </Field>
            <Field label="Status">
              <select data-testid="i-job-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Open">Open (accepting applications)</option>
                <option value="Closed">Closed</option>
              </select>
            </Field>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="i-job-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update" : "Post Job")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applications dialog */}
      <Dialog open={!!appsFor} onOpenChange={(o) => !o && (setAppsFor(null), setApps([]))}>
        <DialogContent className="bg-[#fbf8f1] max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Applications</DialogTitle>
            <DialogDescription>{appsFor?.title} · {apps.length} received</DialogDescription>
          </DialogHeader>
          {appsLoading ? (
            <div className="text-sm text-[#6b655b]">Loading…</div>
          ) : apps.length === 0 ? (
            <div className="bg-white border border-[#e5e0d8] rounded-sm p-8 text-center">
              <FileText size={30} className="mx-auto text-[#bcb6aa]" />
              <div className="mt-2 text-sm text-[#6b655b]">No applications yet.</div>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white border border-[#e5e0d8] rounded-sm">
              <table className="w-full text-sm">
                <thead className="bg-[#f5f1e8] text-[10px] uppercase tracking-wider text-[#6b655b]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Candidate</th>
                    <th className="text-left px-3 py-2 font-semibold">Contact</th>
                    <th className="text-left px-3 py-2 font-semibold">Yrs Exp</th>
                    <th className="text-left px-3 py-2 font-semibold">Current Profession</th>
                    <th className="text-left px-3 py-2 font-semibold">Resume</th>
                    <th className="text-left px-3 py-2 font-semibold">Received</th>
                    <th className="text-left px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} data-testid={`i-app-row-${a.id}`} className="border-t border-[#e5e0d8] align-top">
                      <td className="px-3 py-3 font-semibold text-[#1c1a17]">{a.full_name}</td>
                      <td className="px-3 py-3">
                        <a href={`tel:${a.mobile}`} className="inline-flex items-center gap-1 text-[#234333] hover:text-[#d4a24c]"><Phone size={11} />{a.mobile}</a>
                        {a.email && <a href={`mailto:${a.email}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#6b655b] hover:text-[#d4a24c] break-all"><Mail size={10} />{a.email}</a>}
                      </td>
                      <td className="px-3 py-3">{a.years_experience}</td>
                      <td className="px-3 py-3 text-xs text-[#5b5650] max-w-xs"><div className="line-clamp-3 whitespace-pre-line">{a.current_profession || "—"}</div></td>
                      <td className="px-3 py-3">
                        {a.resume_url ? (
                          <a href={a.resume_url} target="_blank" rel="noopener noreferrer" data-testid={`i-app-resume-${a.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:text-[#d4a24c] text-[11px] font-semibold"><ExternalLink size={11} /> {a.resume_filename || "Resume.pdf"}</a>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-3 text-[11px] text-[#6b655b] whitespace-nowrap">{fmt(a.submitted_at)}</td>
                      <td className="px-3 py-3">
                        <button type="button" onClick={() => setConfirmDeleteApp(a)} data-testid={`i-app-delete-${a.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 px-2 py-1 rounded-sm"><Trash2 size={11} /> Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete Job?</AlertDialogTitle>
            <AlertDialogDescription>“{confirmDelete?.title}” and all its applications will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="i-job-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDeleteApp} onOpenChange={(o) => !o && setConfirmDeleteApp(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete Application?</AlertDialogTitle>
            <AlertDialogDescription>The application from “{confirmDeleteApp?.full_name}” will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeApp} data-testid="i-app-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
